import { useEffect, useState, useRef } from "react";
import { useParams, useLocation, useSearchParams, Link, useNavigate } from "react-router-dom";
import { api, apiErr, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DeleteCasesButton, RestoreChoices } from "@/components/CaseDeleteRestore";
import { CaseOwnership, ImportantTag, ImportantToggle, TaskImportantToggle } from "@/components/CaseOwnership";
import { useStaffNames } from "@/hooks/useStaffNames";
import { Loader, Card, EmptyState } from "@/components/ui-helpers";
import { StatusBadge } from "@/components/StatusBadge";
import { StageTimeline } from "@/components/StageTimeline";
import { CaseFeesRetainer } from "@/components/CaseFeesRetainer";
import { TeamLinkedMessages } from "@/components/TeamLinkedMessages";
import { CaseNoteLog } from "@/components/ClientNoteLog";
import { CaseReassign } from "@/components/CaseReassign";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ClosingSummaryDialog, ClosingSummaryBody, summaryMoney } from "@/components/ClosingSummary";
import { FEATURES } from "@/lib/features";
import { CaseHoldControl, HoldReasonBanner } from "@/components/CaseHoldControl";
import { CaseQuestionnaire } from "@/components/CaseQuestionnaire";
import { toast } from "sonner";
import {
  FileText, Upload, Download, CheckCircle2, Plus, ClipboardList, ListTodo,
  ChevronLeft, Loader2, Trash2, FileCheck2, Eye, Archive, Send, Gavel, Activity,
  RotateCcw, Lock, CheckCircle,
} from "lucide-react";

const CLOSED_CASE_STATUSES = ["Completed", "Cancelled", "Closed", "Refused", "Withdrawn"];
const CLOSED_STAGES = ["Closed", "Completed"]; // "Completed" is the old name of the final stage
const CLOSE_OUTCOMES = ["Approved", "Refused", "Withdrawn", "Closed"];

const DOC_STATUSES = ["Requested", "Under Review", "Accepted", "Rejected", "Replacement Required", "Expired"];
const RECEIVED = ["Uploaded", "Under Review", "Accepted"];

export default function ApplicationDetail() {
  const { id } = useParams();
  const loc = useLocation();
  const { user } = useAuth();
  const isStaff = user?.role !== "client";
  const canEditCase = user?.role === "admin" || (user?.permissions || []).includes("edit_cases");
  const nav = useNavigate();
  const base = loc.pathname.startsWith("/admin") ? "/admin" : "/portal";
  const [app, setApp] = useState(null);
  const names = useStaffNames();
  const [docs, setDocs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [stages, setStages] = useState([]);
  const [templates, setTemplates] = useState([]);
  // the open tab lives in the URL (?tab=fees) so a refresh or a shared link lands back on it
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab") || "overview";
  const tab = rawTab === "documents" && !FEATURES.documents ? "overview" : rawTab;
  const setTab = (t) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    if (t === "overview") next.delete("tab"); else next.set("tab", t);
    return next;
  }, { replace: true });
  const [tabOrder, setTabOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem("caseTabOrder") || "[]"); } catch { return []; }
  });
  const [dragKey, setDragKey] = useState(null);
  const [filesPanel, setFilesPanel] = useState(null); // Case files panel: "submission" | "decision" | null
  const [closeMenu, setCloseMenu] = useState(false);
  const [closing, setClosing] = useState(false);
  const [closeDate, setCloseDate] = useState("");
  const [closeNote, setCloseNote] = useState("");
  const [closeFees, setCloseFees] = useState(null); // fee snapshot for the unpaid-balance warning
  const [summary, setSummary] = useState(null); // closing summary dialog
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");

  const load = () => {
    api.get(`/applications/${id}`).then((r) => setApp(r.data)).catch(() => {});
    api.get(`/applications/${id}/documents`).then((r) => setDocs(r.data)).catch(() => {});
    if (isStaff) {
      api.get(`/applications/${id}/tasks`).then((r) => setTasks(r.data)).catch(() => {});
      api.get("/checklist-templates").then((r) => setTemplates(r.data)).catch(() => {});
    }
  };
  useEffect(() => { load(); api.get("/settings").then((r) => setStages(r.data.stages || [])); }, [id]);

  if (!app) return <Loader />;
  const received = docs.filter((d) => RECEIVED.includes(d.status)).length;

  const changeStage = async (stage) => {
    try {
      const { data } = await api.put(`/applications/${id}`, { current_stage: stage }); toast.success("Stage updated");
      if (data.client_moved) setTimeout(() => toast.info(data.client_moved.to === "closed" ? `${data.client_moved.name} moved to Closed — ${data.client_moved.reason}` : `${data.client_moved.name} is back in Active clients`, { duration: 6000 }), 700);
      load();
      const lower = (stage || "").toLowerCase();
      if (lower.includes("submit")) { setTab("overview"); setFilesPanel("submission"); toast("Now upload the submission letter for this case.", { icon: "📨" }); }
      else if (lower.includes("decision")) { setTab("overview"); setFilesPanel("decision"); toast("Now record the officer's decision for this case.", { icon: "⚖️" }); }
      else return;
      setTimeout(() => document.getElementById("case-files-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    }
    catch (e) { toast.error(apiErr(e)); }
  };

  const restoreTo = async (to) => {
    try { const { data } = await api.patch(`/applications/${id}/archive`, { archived: false, restore_to: to }); setApp({ ...app, ...data }); toast.success(`Case restored to ${to === "completed" ? "Closed" : "Active"}`); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggleArchive = async () => {
    const goingToArchive = !app.archived;
    if (goingToArchive && !window.confirm(`Archive this case (${app.app_number})? It'll be removed from the list but kept — restore it anytime from the Archived filter.`)) return;
    try {
      const { data } = await api.patch(`/applications/${id}/archive`, { archived: goingToArchive });
      setApp(data);
      toast.success(goingToArchive ? "Case archived" : "Case restored");
      if (goingToArchive) nav(`${base}/applications`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openTasks = tasks.filter((t) => !["Completed", "Cancelled"].includes(t.status)).length;
  const isClosed = CLOSED_CASE_STATUSES.includes(app.status) || CLOSED_STAGES.includes(app.current_stage);

  const unpaid = closeFees && closeFees.configured && Number(closeFees.balance || 0) > 0;
  const openCloseMenu = () => {
    setCloseMenu((v) => !v);
    api.get(`/applications/${id}/fees`).then((r) => setCloseFees(r.data)).catch(() => setCloseFees(null));
  };
  const closeCase = async (outcome) => {
    if (unpaid && !window.confirm(`This case still has an unpaid balance of ${summaryMoney(closeFees.balance, closeFees.currency)}. Close it anyway?`)) return;
    setClosing(true);
    try {
      const { data } = await api.post(`/applications/${id}/close`, { outcome, decision_date: closeDate || null, note: closeNote || "" });
      setApp(data); setCloseMenu(false); setCloseNote(""); setCloseDate("");
      toast.success(`Case closed (${outcome})`);
      if (data.closing_summary) setSummary(data.closing_summary);
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setClosing(false); }
  };
  const reopenCase = async () => {
    const reason = reopenReason.trim();
    if (reason.length < 3) { toast.error("Please explain why this case is being reopened"); return; }
    setClosing(true);
    try {
      const { data } = await api.post(`/applications/${id}/reopen`, { reason });
      setApp(data); setReopenOpen(false); setReopenReason("");
      toast.success("Case reopened");
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setClosing(false); }
  };

  return (
    <div className="space-y-6">
      <Link to={`${base}/applications`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-primary"><ChevronLeft className="h-4 w-4" /> Back to applications</Link>

      <Card className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 data-testid="case-title" className="font-head font-extrabold text-2xl text-slate-900">{app.case_name || app.service_type}</h1>
              <StatusBadge status={app.status} />
              <StatusBadge status={app.priority} />
              {app.important && <ImportantTag />}
            </div>
            <div className="font-mono text-sm text-slate-400 mt-1">{app.app_number} {app.client_name && `· ${app.client_name}`}{app.case_name && <span className="font-sans"> · {app.service_type}</span>}</div>
            {isStaff && <CaseOwnership app={app} names={names} className="mt-1.5 text-sm" />}
            <HoldReasonBanner app={app} />
            {app.archived && <div className="mt-1.5"><span data-testid="case-archived-badge" className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full"><Archive className="h-3 w-3" /> Archived</span></div>}
            {!isClosed && app.last_reopen && (
              <div data-testid="case-reopened-banner" className="mt-1.5 max-w-xl text-xs rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-2.5 py-1.5">
                <span className="font-semibold inline-flex items-center gap-1"><RotateCcw className="h-3 w-3" /> Reopened</span> by {app.last_reopen.by_name} · {new Date(app.last_reopen.at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                <div data-testid="case-reopened-reason" className="mt-0.5 text-amber-900">“{app.last_reopen.reason}”</div>
              </div>
            )}
            {isClosed && !app.archived && <div className="mt-1.5"><span data-testid="case-closed-badge" className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full"><CheckCircle className="h-3 w-3" /> Closed{app.closed_outcome ? ` · ${app.closed_outcome}` : ""}{app.auto_closed ? " · auto" : ""}</span></div>}
          </div>
          {isStaff && (
            <div className="flex items-center gap-2 flex-wrap">
              <select data-testid="stage-select" value={app.current_stage} disabled={isClosed} onChange={(e) => changeStage(e.target.value)} title={isClosed ? "Reopen the case to change its stage" : "Move the case to a stage"} className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium focus:ring-2 focus:ring-primary/30 disabled:bg-slate-50 disabled:text-slate-400">
                {(() => {
                  // "Closed" is reached only via Close case (or automatically) — keep it out of the manual list
                  const pick = (stages.includes(app.current_stage) ? stages : [app.current_stage, ...stages].filter(Boolean));
                  return pick.filter((s) => !CLOSED_STAGES.includes(s) || s === app.current_stage).map((s) => <option key={s} value={s}>{s}</option>);
                })()}
              </select>
              {canEditCase && !app.archived && !isClosed && <CaseHoldControl app={app} onChange={(data) => { setApp({ ...app, ...data }); load(); }} />}
              {canEditCase && !app.archived && <CaseReassign app={app} onDone={load} />}
              {canEditCase && <ImportantToggle app={app} onChange={(v) => setApp({ ...app, important: v })} />}
              {canEditCase && !app.archived && (isClosed ? (
                <button data-testid="case-reopen-btn" onClick={() => { setReopenReason(""); setReopenOpen(true); }} disabled={closing} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 disabled:opacity-50">
                  <RotateCcw className="h-4 w-4" /> Reopen
                </button>
              ) : (
                <div className="relative">
                  <button data-testid="case-close-btn" onClick={openCloseMenu} disabled={closing}
                    title={openTasks ? `Finish ${openTasks} open task(s) first` : "Close this case"}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                    {openTasks ? <Lock className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />} Close case
                  </button>
                  {closeMenu && (
                    <div data-testid="case-close-menu" className="absolute right-0 z-20 mt-2 w-72 rounded-xl border border-slate-200 bg-white shadow-lg p-3">
                      {openTasks > 0 ? (
                        <div className="px-1 py-1 text-sm text-slate-600">
                          <div className="font-semibold text-slate-800 flex items-center gap-1.5"><Lock className="h-4 w-4 text-amber-600" /> {openTasks} open task{openTasks > 1 ? "s" : ""}</div>
                          <p className="mt-1 text-xs text-slate-500">Finish or cancel every task on this case before you can close it.</p>
                          <button onClick={() => { setCloseMenu(false); setTab("tasks"); }} className="mt-2 text-xs font-semibold text-primary hover:underline">Go to tasks →</button>
                        </div>
                      ) : (
                        <>
                          <div className="pb-1 text-xs font-semibold text-slate-400 uppercase tracking-wide">Close & record decision</div>
                          <p className="text-[11px] text-slate-500 mb-2 leading-snug">This finishes the case, files the decision, and clears it from every staff dashboard.</p>
                          {unpaid && (
                            <div data-testid="close-unpaid-warning" className="mb-2 rounded-lg bg-rose-50 border border-rose-200 px-2.5 py-2 text-[11px] text-rose-800 leading-snug">
                              <div className="font-semibold flex items-center gap-1"><Lock className="h-3.5 w-3.5" /> Unpaid balance: {summaryMoney(closeFees.balance, closeFees.currency)}</div>
                              Paid {summaryMoney(closeFees.paid, closeFees.currency)} of {summaryMoney(closeFees.total, closeFees.currency)}. You can still close — the balance is recorded in the closing summary.
                            </div>
                          )}
                          <label className="text-[11px] font-medium text-slate-500">Decision date (optional)</label>
                          <input type="date" data-testid="close-decision-date" value={closeDate} onChange={(e) => setCloseDate(e.target.value)}
                            className="mt-0.5 mb-2 w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm" />
                          <label className="text-[11px] font-medium text-slate-500">Note (optional)</label>
                          <textarea data-testid="close-decision-note" rows={2} value={closeNote} onChange={(e) => setCloseNote(e.target.value)}
                            placeholder="e.g. PR approved, COPR issued" className="mt-0.5 mb-2 w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm resize-none" />
                          <div className="text-[11px] font-medium text-slate-500 mb-1">Outcome — pick one to close:</div>
                          <div className="grid grid-cols-2 gap-1.5">
                            {CLOSE_OUTCOMES.map((o) => (
                              <button key={o} data-testid={`close-outcome-${o}`} disabled={closing} onClick={() => closeCase(o)}
                                className="px-2 py-2 rounded-lg text-sm font-semibold border border-slate-200 text-slate-700 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 disabled:opacity-50">{o}</button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {canEditCase && (app.archived ? <RestoreChoices size="md" onPick={restoreTo} idSuffix="-detail" /> : (
                <button data-testid="case-archive-btn" onClick={toggleArchive} title="Hide this case from your lists but keep everything — restore it anytime" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm bg-slate-100 text-slate-600">
                  <Archive className="h-4 w-4" /> Archive
                </button>
              ))}
              {canEditCase && <DeleteCasesButton variant="button" testid="case-delete-btn" cases={[app]} onDeleted={() => nav(`${base}/applications`)} onRestored={() => nav(`/admin/applications/${id}`)} />}
            </div>
          )}
        </div>
      </Card>

      {/* Tabs — drag to reorder (saved per browser) */}
      {(() => {
        const labels = { overview: "Overview", documents: `Documents (${received}/${docs.length})`, tasks: `Tasks (${tasks.length})`, fees: "Fees", retainer: "Retainer", questionnaire: "Questionnaire" };
        const baseKeys = ["overview", ...(FEATURES.documents ? ["documents"] : []), ...(isStaff ? ["tasks", "fees", "retainer", "questionnaire"] : [])];
        const orderedKeys = [...tabOrder.filter((k) => baseKeys.includes(k)), ...baseKeys.filter((k) => !tabOrder.includes(k))];
        const onDrop = (targetKey) => {
          if (!dragKey || dragKey === targetKey) { setDragKey(null); return; }
          const cur = orderedKeys.slice();
          cur.splice(cur.indexOf(dragKey), 1);
          cur.splice(cur.indexOf(targetKey), 0, dragKey);
          setTabOrder(cur); localStorage.setItem("caseTabOrder", JSON.stringify(cur)); setDragKey(null);
        };
        return (
          <div className="flex gap-1 border-b border-slate-200 overflow-x-auto hide-scrollbar">
            {orderedKeys.map((k) => (
              <button key={k} data-testid={`tab-${k}`} draggable title="Drag to reorder"
                onDragStart={() => setDragKey(k)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(k)} onDragEnd={() => setDragKey(null)}
                onClick={() => setTab(k)}
                className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors cursor-grab active:cursor-grabbing ${dragKey === k ? "opacity-40" : ""} ${tab === k ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{labels[k]}</button>
            ))}
          </div>
        );
      })()}

      <ClosingSummaryDialog summary={summary} onClose={() => setSummary(null)} />
      <Dialog open={reopenOpen} onOpenChange={(o) => { if (!closing) setReopenOpen(o); }}>
        <DialogContent className="sm:max-w-md" data-testid="reopen-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><RotateCcw className="h-5 w-5 text-amber-600" /> Reopen this case?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">It moves back to the Active folder{app.closed_outcome ? ` (was closed: ${app.closed_outcome})` : ""}. Explain why — the reason is saved on the case and in its notes.</p>
          <label className="text-xs font-medium text-slate-500">Reason for reopening *</label>
          <textarea data-testid="reopen-reason" rows={3} autoFocus value={reopenReason} onChange={(e) => setReopenReason(e.target.value)}
            placeholder="e.g. IRCC requested additional documents after the decision"
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-primary/30" />
          <div className="flex gap-2">
            <button data-testid="reopen-cancel" onClick={() => setReopenOpen(false)} disabled={closing} className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold">Cancel</button>
            <button data-testid="reopen-confirm" onClick={reopenCase} disabled={closing || reopenReason.trim().length < 3}
              className="flex-1 px-4 py-2.5 rounded-xl bg-amber-600 text-white text-sm font-semibold disabled:opacity-50">{closing ? "Reopening…" : "Reopen case"}</button>
          </div>
        </DialogContent>
      </Dialog>
      {tab === "fees" && isStaff && <CaseFeesRetainer app={app} onAppChanged={load} view="fees" />}
      {tab === "retainer" && isStaff && <CaseFeesRetainer app={app} onAppChanged={load} view="retainer" />}
      {tab === "questionnaire" && isStaff && <CaseQuestionnaire appId={id} canEdit={canEditCase} />}

      {tab === "overview" && (
        <div className="space-y-6">
          {isStaff && <div id="case-files-anchor"><CaseFilesSection app={app} reload={load} canEdit={canEditCase} panel={filesPanel} onToggle={(k) => setFilesPanel((p) => (p === k ? null : k))} /></div>}
          <div className="grid lg:grid-cols-3 gap-6">
          <Card className="p-6 lg:col-span-2"><StageTimeline stages={stages} current={app.current_stage} /></Card>
          <div className="space-y-4">
            <Card className="p-5">
              <h3 className="font-head font-bold text-slate-900 mb-3">Details</h3>
              <dl className="space-y-2 text-sm">
                {app.case_name && <Row label="Case name" value={app.case_name} />}
                <Row label="Service" value={app.service_type} />
                <Row label="Opened" value={`${(app.date_opened || "").slice(0, 10)}${app.created_by ? ` · by ${app.created_by}` : ""}`} />
                <Row label="Target Submission" value={app.target_submission_date || "—"} />
                <Row label="UCI" value={app.uci || "—"} />
                <Row label="IRCC Number" value={app.ircc_number || "—"} />
              </dl>
            </Card>
            {!isStaff && (app.client_notes || app.notes) && (
              <Card className="p-5">
                <h3 className="font-head font-bold text-slate-900 mb-2">Notes from your consultant</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{app.client_notes || app.notes}</p>
              </Card>
            )}
            {isStaff && isClosed && app.closing_summary && (
              <Card className="p-5" data-testid="closing-summary-card">
                <h3 className="font-head font-bold text-slate-900 mb-2">Closing summary</h3>
                <ClosingSummaryBody s={app.closing_summary} />
              </Card>
            )}
            {isStaff && (
              <>
                <CaseNotesCard app={app} canManage={canEditCase} onAdded={() => setTimeout(load, 3000)} />
                <TeamLinkedMessages applicationId={app.id} />
              </>
            )}
          </div>
          </div>
          {isStaff && <CaseActivity appId={id} refreshKey={`${app.current_stage}|${(app.submission_files || []).length}|${(app.decision_files || []).length}|${app.decision_outcome || ""}`} />}
        </div>
      )}

      {tab === "documents" && FEATURES.documents && (
        <DocumentsTab id={id} docs={docs} isStaff={isStaff} templates={templates} received={received} reload={load} />
      )}

      {tab === "tasks" && isStaff && (
        <TasksTab id={id} tasks={tasks} reload={load} onAutoClosed={setSummary} />
      )}
    </div>
  );
}

function CaseFileList({ app, slot, reload, canEdit, emptyText }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const files = (slot === "submission" ? app.submission_files : app.decision_files) || [];
  const upload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try { await api.post(`/applications/${app.id}/case-files/${slot}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("File uploaded"); reload(); }
    catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const del = async (fid) => {
    if (!window.confirm("Remove this file?")) return;
    try { await api.delete(`/applications/${app.id}/case-files/${slot}/${fid}`); toast.success("File removed"); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="space-y-2">
      {files.length === 0 ? (
        <p className="text-sm text-slate-400">{emptyText}</p>
      ) : files.map((f) => (
        <div key={f.file_id} data-testid={`${slot}-file-${f.file_id}`} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200">
          <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><FileText className="h-4 w-4 text-primary" /></div>
          <button onClick={() => downloadFile(`/applications/${app.id}/case-files/${slot}/${f.file_id}/download`, f.file_name)} data-testid={`${slot}-download-${f.file_id}`} className="flex-1 min-w-0 text-left group">
            <div className="text-sm font-semibold text-slate-800 truncate group-hover:text-primary flex items-center gap-1.5"><Download className="h-3.5 w-3.5 text-slate-400" /> {f.file_name}</div>
            <div className="text-[11px] text-slate-400 mt-0.5">{Math.round(f.size / 1024)} KB · {(f.uploaded_at || "").slice(0, 10)} · {f.uploaded_by}</div>
          </button>
          {canEdit && <button onClick={() => del(f.file_id)} data-testid={`${slot}-delete-${f.file_id}`} className="text-slate-400 hover:text-rose-500 shrink-0" title="Remove"><Trash2 className="h-4 w-4" /></button>}
        </div>
      ))}
      {canEdit && (
        <>
          <input ref={fileRef} type="file" hidden accept=".pdf" onChange={upload} data-testid={`${slot}-file-input`} />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`${slot}-upload-btn`} className="mt-1 inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-xl text-sm font-semibold disabled:opacity-60">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload PDF
          </button>
        </>
      )}
    </div>
  );
}

function CaseFilesSection({ app, reload, canEdit, panel, onToggle }) {
  const subCount = (app.submission_files || []).length;
  const btn = (key, Icon, label) => (
    <button data-testid={`case-files-${key}-btn`} onClick={() => onToggle(key)}
      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm border transition-colors ${panel === key ? "bg-primary text-white border-primary" : "bg-white text-slate-700 border-slate-200 hover:border-primary/40"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
  return (
    <Card className="p-6" data-testid="case-files-section">
      <h3 className="font-head font-bold text-slate-900 mb-1">Case files</h3>
      <p className="text-xs text-slate-500 mb-4">Submission letter and the officer's decision for this case.</p>
      <div className="flex flex-wrap gap-2">
        {btn("submission", Send, `Submission letter${subCount ? ` (${subCount})` : ""}`)}
        {btn("decision", Gavel, app.decision_outcome ? `Decision · ${app.decision_outcome}` : "Decision")}
      </div>
      {panel === "submission" && <div className="mt-5" data-testid="submission-panel"><SubmissionBody app={app} reload={reload} canEdit={canEdit} /></div>}
      {panel === "decision" && <div className="mt-5" data-testid="decision-panel"><DecisionBody app={app} reload={reload} canEdit={canEdit} /></div>}
    </Card>
  );
}

function SubmissionBody({ app, reload, canEdit }) {
  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-2 mb-1"><Send className="h-4 w-4 text-primary" /><h4 className="font-head font-bold text-slate-900">Submission letter</h4></div>
      <p className="text-xs text-slate-500 mb-4">Upload the submission letter(s) sent to IRCC for this case (PDF only). Moving the case stage to "Application Submitted" places it in the Submitted folder.</p>
      <CaseFileList app={app} slot="submission" reload={reload} canEdit={canEdit} emptyText="No submission letter uploaded yet." />
    </div>
  );
}

const ACTIVITY_LABEL = {
  application_create: () => "created this case",
  application_stage_change: (dt) => `changed stage: ${dt}`,
  application_update: () => "updated the case details",
  application_bulk_update: () => "updated the case",
  case_submission_upload: (dt) => `uploaded a submission letter${dt ? ` (${dt})` : ""}`,
  case_submission_delete: () => "removed a submission file",
  case_decision_upload: (dt) => `uploaded a decision document${dt ? ` (${dt})` : ""}`,
  case_decision_delete: () => "removed a decision file",
  case_decision: (dt) => `recorded the decision${dt && dt !== "updated" ? `: ${dt}` : ""}`,
};

function CaseActivity({ appId, refreshKey }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/applications/${appId}/activity`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [appId, refreshKey]);
  const when = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
  return (
    <Card className="p-6" data-testid="case-activity">
      <div className="flex items-center gap-2 mb-1"><Activity className="h-4 w-4 text-primary" /><h3 className="font-head font-bold text-slate-900">Activity log</h3></div>
      <p className="text-xs text-slate-500 mb-4">Everything that's happened on this case — who did it and when (stage changes, uploads, decision).</p>
      {items === null ? <div className="text-sm text-slate-400">Loading…</div>
        : items.length === 0 ? <div className="text-sm text-slate-400">No activity recorded yet.</div>
        : <ul className="space-y-3">
            {items.map((it) => {
              const fn = ACTIVITY_LABEL[it.action];
              const text = fn ? fn(it.detail) : (it.action || "").replace(/_/g, " ");
              return (
                <li key={it.id} data-testid={`activity-${it.id}`} className="flex items-start gap-3 text-sm">
                  <span className="mt-0.5 h-7 w-7 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center"><Activity className="h-3.5 w-3.5" /></span>
                  <div className="min-w-0 flex-1">
                    <div className="text-slate-800"><span className="font-semibold">{it.user_name || "Someone"}</span> <span className="text-slate-600">{text}</span></div>
                    <div className="text-xs text-slate-400 mt-0.5">{when(it.created_at)}</div>
                  </div>
                </li>
              );
            })}
          </ul>}
    </Card>
  );
}
function DecisionBody({ app, reload, canEdit }) {
  // Read-only summary. The decision is recorded in one place now — the "Close case" action.
  const outcome = app.decision_outcome || app.closed_outcome || "";
  const decided = !!outcome;
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-2 mb-1"><Gavel className="h-4 w-4 text-primary" /><h4 className="font-head font-bold text-slate-900">Decision</h4></div>
        {decided ? (
          <>
            <p className="text-xs text-slate-500 mb-4">Recorded when the case was closed. To change it, reopen the case and close it again.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><div className="text-xs font-medium text-slate-500">Outcome</div><div data-testid="decision-outcome-value" className="mt-1 text-sm font-semibold text-slate-800">{outcome}{app.auto_closed ? " · auto" : ""}</div></div>
              <div><div className="text-xs font-medium text-slate-500">Decision date</div><div className="mt-1 text-sm text-slate-800">{app.decision_date || "—"}</div></div>
            </div>
            {app.decision_notes && <><div className="text-xs font-medium text-slate-500 mt-4">Notes</div><p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{app.decision_notes}</p></>}
          </>
        ) : (
          <p data-testid="decision-empty" className="text-sm text-slate-500 mt-2">No decision recorded yet. Use <span className="font-semibold text-slate-700">Close case</span> (top-right) to record the outcome and finish this case.</p>
        )}
      </div>
      <div className="rounded-xl border border-slate-200 p-4">
        <h4 className="font-head font-bold text-slate-900 mb-1">Decision documents</h4>
        <p className="text-xs text-slate-500 mb-4">Upload the decision letter, PPR or officer notes (PDF only).</p>
        <CaseFileList app={app} slot="decision" reload={reload} canEdit={canEdit} emptyText="No decision documents uploaded yet." />
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-2"><dt className="text-slate-400">{label}</dt><dd className="font-medium text-slate-700 text-right">{value}</dd></div>
);

// Case notes: a running, timestamped log (who + when) — replaces the old single "Staff Notes" box.
function CaseNotesCard({ app, canManage, onAdded }) {
  return (
    <Card className="p-5" data-testid="case-notes-card">
      <CaseNoteLog key={`${app.assigned_staff || ""}|${app.reassigned_at || ""}`} appId={app.id} canManage={canManage} onAdded={onAdded} />
      {app.ai_note_flag && (
        <div data-testid="note-ai-flag" className={`mt-2 text-[11px] rounded-lg px-2.5 py-1.5 ${app.ai_note_flag.important ? "bg-amber-50 text-amber-800 border border-amber-200" : "text-slate-400"}`}>
          {app.ai_note_flag.important ? `AI flagged the last note as important — assigned team notified (${(app.ai_note_flag.at || "").slice(0, 10)}).` : "Last note checked by AI: routine, no team notification sent."}
        </div>
      )}
    </Card>
  );
}

function DocumentsTab({ id, docs, isStaff, templates, received, reload }) {
  const [adding, setAdding] = useState(false);
  const [tpl, setTpl] = useState("");
  const pct = docs.length ? Math.round((received / docs.length) * 100) : 0;

  const applyTemplate = async () => {
    if (!tpl) return;
    try { const r = await api.post(`/applications/${id}/apply-checklist/${tpl}`); toast.success(`${r.data.count} documents requested`); setTpl(""); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="flex items-center gap-4">
          <div className="relative h-16 w-16 shrink-0">
            <svg className="h-16 w-16 -rotate-90"><circle cx="32" cy="32" r="28" fill="none" stroke="#E2E8F0" strokeWidth="6" /><circle cx="32" cy="32" r="28" fill="none" stroke="#1479C4" strokeWidth="6" strokeDasharray={`${2 * Math.PI * 28}`} strokeDashoffset={`${2 * Math.PI * 28 * (1 - pct / 100)}`} strokeLinecap="round" /></svg>
            <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-slate-900">{pct}%</span>
          </div>
          <div className="flex-1">
            <div className="font-head font-bold text-lg text-slate-900" data-testid="doc-progress">{received} of {docs.length} documents received</div>
            <p className="text-sm text-slate-500">Upload the remaining documents to keep your application moving.</p>
          </div>
          {isStaff && (
            <div className="flex flex-col sm:flex-row gap-2">
              <select value={tpl} onChange={(e) => setTpl(e.target.value)} data-testid="template-select" className="px-3 py-2 rounded-lg border border-slate-200 text-sm">
                <option value="">Apply checklist...</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <button onClick={applyTemplate} disabled={!tpl} data-testid="apply-template" className="px-3 py-2 bg-secondary text-white rounded-lg text-sm font-semibold disabled:opacity-40 flex items-center gap-1"><ClipboardList className="h-4 w-4" /> Apply</button>
              <button onClick={() => setAdding(true)} data-testid="add-doc-req" className="px-3 py-2 bg-primary text-white rounded-lg text-sm font-semibold flex items-center gap-1"><Plus className="h-4 w-4" /> Add</button>
            </div>
          )}
        </div>
      </Card>

      {adding && <AddDocReq id={id} onDone={() => { setAdding(false); reload(); }} onCancel={() => setAdding(false)} />}

      {docs.length === 0 ? (
        <Card className="p-6"><EmptyState icon={FileText} title="No documents yet" description={isStaff ? "Apply a checklist template or add a document requirement." : "Your consultant will request documents here soon."} /></Card>
      ) : (
        <div className="grid gap-3">
          {docs.map((d) => <DocRow key={d.id} doc={d} isStaff={isStaff} reload={reload} />)}
        </div>
      )}
    </div>
  );
}

function AddDocReq({ id, onDone, onCancel }) {
  const [f, setF] = useState({ name: "", category: "General", description: "", required: true });
  const save = async () => {
    if (!f.name) return;
    try { await api.post(`/applications/${id}/documents`, f); toast.success("Document requirement added"); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Card className="p-5 border-primary/30">
      <div className="grid sm:grid-cols-2 gap-3">
        <input placeholder="Document name" data-testid="doc-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
        <input placeholder="Category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
        <input placeholder="Description / instructions" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200 text-sm sm:col-span-2" />
      </div>
      <label className="flex items-center gap-2 mt-3 text-sm text-slate-600"><input type="checkbox" checked={f.required} onChange={(e) => setF({ ...f, required: e.target.checked })} /> Required</label>
      <div className="flex gap-2 mt-3">
        <button onClick={save} data-testid="save-doc-req" className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold">Add</button>
        <button onClick={onCancel} className="px-4 py-2 border border-slate-200 rounded-lg text-sm">Cancel</button>
      </div>
    </Card>
  );
}

function DocRow({ doc, isStaff, reload }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [reviewing, setReviewing] = useState(false);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try { await api.post(`/documents/${doc.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("File uploaded"); reload(); }
    catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); }
  };

  return (
    <Card className="p-4">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><FileText className="h-5 w-5 text-slate-500" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900">{doc.name}</span>
            {doc.required ? <span className="text-[10px] font-bold text-rose-500">REQUIRED</span> : <span className="text-[10px] font-medium text-slate-400">OPTIONAL</span>}
            <StatusBadge status={doc.status} />
          </div>
          {doc.description && <p className="text-xs text-slate-500 mt-0.5">{doc.description}</p>}
          <div className="text-[11px] text-slate-400 mt-0.5">{doc.category}{doc.reviewed_by && ` · reviewed by ${doc.reviewed_by}`}</div>
          {doc.staff_comments && <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-2 py-1 mt-2">Staff: {doc.staff_comments}</p>}

          {doc.files?.length > 0 && (
            <div className="mt-2 space-y-1">
              {doc.files.map((f) => (
                <button key={f.file_id} onClick={() => downloadFile(`/documents/${doc.id}/download/${f.file_id}`, f.file_name)} data-testid={`download-${f.file_id}`} className="flex items-center gap-2 text-xs text-primary hover:underline">
                  <Download className="h-3.5 w-3.5" /> {f.file_name} <span className="text-slate-400">({Math.round(f.size / 1024)} KB)</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <input ref={fileRef} type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={upload} data-testid={`file-input-${doc.id}`} />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`upload-${doc.id}`} className="px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-semibold flex items-center gap-1 disabled:opacity-50">
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload
          </button>
          {isStaff && <button onClick={() => setReviewing(!reviewing)} data-testid={`review-${doc.id}`} className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold flex items-center gap-1"><FileCheck2 className="h-3.5 w-3.5" /> Review</button>}
        </div>
      </div>
      {reviewing && isStaff && <ReviewForm doc={doc} onDone={() => { setReviewing(false); reload(); }} />}
    </Card>
  );
}

function ReviewForm({ doc, onDone }) {
  const [status, setStatus] = useState(doc.status);
  const [comment, setComment] = useState(doc.staff_comments || "");
  const save = async () => {
    try { await api.put(`/documents/${doc.id}/review`, { status, staff_comments: comment }); toast.success("Document reviewed"); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="mt-3 pt-3 border-t border-slate-100 flex flex-col sm:flex-row gap-2">
      <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm">
        {DOC_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Comment to client" className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm" />
      <button onClick={save} data-testid={`save-review-${doc.id}`} className="px-4 py-2 bg-secondary text-white rounded-lg text-sm font-semibold">Save</button>
    </div>
  );
}

const TASK_STATUSES = ["To Do", "In Progress", "Waiting for Client", "Completed", "Cancelled"];
function TasksTab({ id, tasks, reload, onAutoClosed }) {
  const names = useStaffNames();
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ title: "", priority: "Normal", due_date: "", important: false });
  const add = async () => {
    if (!f.title) return;
    try { await api.post(`/applications/${id}/tasks`, f); toast.success("Task added"); setF({ title: "", priority: "Normal", due_date: "", important: false }); setAdding(false); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const update = async (t, status) => {
    // Finishing the LAST open task closes the case automatically — warn first if money is still owed.
    const isOpen = (x) => !["Completed", "Cancelled"].includes(x.status);
    const lastOpen = ["Completed", "Cancelled"].includes(status) && isOpen(t) && tasks.filter(isOpen).length === 1;
    const willClose = lastOpen && (status === "Completed" || tasks.some((x) => x.id !== t.id && x.status === "Completed"));
    if (willClose) {
      try {
        const { data: fees } = await api.get(`/applications/${id}/fees`);
        if (fees.configured && Number(fees.balance || 0) > 0 &&
          !window.confirm(`This is the last open task, so the case will close automatically — but it still has an unpaid balance of ${summaryMoney(fees.balance, fees.currency)}. Continue?`)) return;
      } catch { /* fee check is advisory only */ }
    }
    try {
      const { data } = await api.put(`/tasks/${t.id}`, { status });
      if (data?.case_auto_closed) {
        toast.success("All tasks are done — the case was closed automatically.");
        if (data.closing_summary) onAutoClosed?.(data.closing_summary);
      }
      reload();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><button onClick={() => setAdding(!adding)} data-testid="add-task" className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold flex items-center gap-1"><Plus className="h-4 w-4" /> Add task</button></div>
      {adding && (
        <Card className="p-4 flex flex-col sm:flex-row gap-2">
          <input placeholder="Task title" data-testid="task-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200 text-sm">{["Low", "Normal", "High", "Urgent"].map((p) => <option key={p}>{p}</option>)}</select>
          <input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          <label className="inline-flex items-center gap-1.5 text-sm font-semibold text-rose-600 px-2"><input type="checkbox" data-testid="task-important-new" checked={f.important} onChange={(e) => setF({ ...f, important: e.target.checked })} className="h-4 w-4 accent-rose-600" /> Important</label>
          <button onClick={add} className="px-4 py-2 bg-secondary text-white rounded-lg text-sm font-semibold">Add</button>
        </Card>
      )}
      {tasks.length === 0 ? <Card className="p-6"><EmptyState icon={ListTodo} title="No tasks yet" /></Card> : [...tasks].sort((a, b) => (b.important ? 1 : 0) - (a.important ? 1 : 0)).map((t) => (
        <Card key={t.id} className={`p-4 flex items-center gap-3 ${t.important ? "border-l-4 border-l-rose-500" : ""}`}>
          <ListTodo className="h-5 w-5 text-slate-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap"><span className="font-semibold text-slate-900">{t.title}</span><StatusBadge status={t.priority} />{t.important && <ImportantTag />}</div>
            <div className="text-xs text-slate-400">{t.due_date ? `Due ${t.due_date} · ` : ""}Assigned to {names[t.assigned_staff] || "—"} · Created by {t.created_by_name || "—"}</div>
          </div>
          <TaskImportantToggle task={t} onChange={() => reload()} />
          <select value={t.status} onChange={(e) => update(t, e.target.value)} data-testid={`task-status-${t.id}`} className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium">{TASK_STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
        </Card>
      ))}
    </div>
  );
}
